import { isEmpty } from 'lodash-es';
import { useMemo, type PropsWithChildren } from 'react';

import { usePieChartConfig } from '../context/PieChartConfigContext.js';
import { usePieChartRawData } from '../context/PieChartRawDataContext.js';
import { PieChartSlicesDataContext } from '../context/PieChartSlicesData.js';
import { defaultGrouping } from '../defaults.js';
import { useSetHighlightedIdsState } from '../hooks/store/use-set-highlighted-ids-state.js';
import { useSetVisibleSlicesState } from '../hooks/store/use-set-visible-slices-state.js';
import { buildGroupThreshold } from '../utils/build-group-threshold.js';

export const PieChartSlicesDataProvider = ({ children }: PropsWithChildren) => {
  const chartData = usePieChartRawData();
  const chartConfig = usePieChartConfig();

  const setHighlightedIdsState = useSetHighlightedIdsState();
  const setVisibleSlicesState = useSetVisibleSlicesState();

  const isDataAvailable = !isEmpty(chartData);

  const slices = useMemo(
    () =>
      isDataAvailable
        ? buildGroupThreshold(
            chartData?.slices,
            chartConfig.grouping ?? defaultGrouping,
            chartConfig.colorPalette,
            chartData?.unit,
          )
        : [],
    [
      chartConfig.colorPalette,
      chartConfig.grouping,
      chartData?.slices,
      chartData?.unit,
      isDataAvailable,
    ],
  );

  const highlightedIds = slices.map((slice) => slice.id);

  setHighlightedIdsState(highlightedIds);
  setVisibleSlicesState(slices);

  return (
    <PieChartSlicesDataContext.Provider value={{ slices }}>
      {children}
    </PieChartSlicesDataContext.Provider>
  );
};
